import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, FileText, Calendar, ChevronRight, CheckCircle, XCircle, Clock, ArrowLeft, Users, ClipboardCheck, UserCheck } from 'lucide-react';

const ReportsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [assetDeployments, setAssetDeployments] = useState([]);
  const [customerDeployments, setCustomerDeployments] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Drill-down state
  const [showDrillDown, setShowDrillDown] = useState(false);
  const [drillDownType, setDrillDownType] = useState(null); // 'asset' or 'customer'
  const [drillDownData, setDrillDownData] = useState(null);
  const [drillDownLoading, setDrillDownLoading] = useState(false);

  // Technician Activity Report state
  const [technicianActivity, setTechnicianActivity] = useState([]);
  const [techActivityLoading, setTechActivityLoading] = useState(false);
  const [techActivityStartDate, setTechActivityStartDate] = useState('');
  const [techActivityEndDate, setTechActivityEndDate] = useState('');
  const [expandedTechnician, setExpandedTechnician] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchDeploymentReports();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/reports/dashboard');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeploymentReports = async () => {
    try {
      const params = {};
      if (startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      
      const [assetRes, customerRes] = await Promise.all([
        api.get('/reports/asset-deployments', { params }),
        api.get('/reports/customer-deployments', { params }),
      ]);
      
      setAssetDeployments(assetRes.data);
      setCustomerDeployments(customerRes.data);
    } catch (error) {
      console.error('Failed to fetch deployment reports:', error);
      toast.error('Failed to load deployment reports');
    }
  };

  const handleDateFilter = () => {
    if (startDate && endDate) {
      fetchDeploymentReports();
      toast.success('Reports filtered by date range');
    } else {
      toast.error('Please select both start and end dates');
    }
  };

  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    fetchDeploymentReports();
    toast.success('Date filter cleared');
  };

  const handleAssetDrillDown = async (assetId) => {
    setDrillDownType('asset');
    setDrillDownLoading(true);
    setShowDrillDown(true);
    try {
      const response = await api.get(`/reports/asset-drill-down/${encodeURIComponent(assetId)}`);
      setDrillDownData(response.data);
    } catch (error) {
      console.error('Failed to fetch asset drill-down:', error);
      toast.error('Failed to load asset history');
      setShowDrillDown(false);
    } finally {
      setDrillDownLoading(false);
    }
  };

  const handleCustomerDrillDown = async (customerName) => {
    setDrillDownType('customer');
    setDrillDownLoading(true);
    setShowDrillDown(true);
    try {
      const response = await api.get(`/reports/customer-drill-down/${encodeURIComponent(customerName)}`);
      setDrillDownData(response.data);
    } catch (error) {
      console.error('Failed to fetch customer drill-down:', error);
      toast.error('Failed to load customer history');
      setShowDrillDown(false);
    } finally {
      setDrillDownLoading(false);
    }
  };

  // Technician Activity Report functions
  const fetchTechnicianActivity = async () => {
    setTechActivityLoading(true);
    try {
      const params = {};
      if (techActivityStartDate && techActivityEndDate) {
        params.start_date = techActivityStartDate;
        params.end_date = techActivityEndDate;
      }
      const response = await api.get('/reports/technician-activity', { params });
      // API returns {technicians: [...], start_date, end_date}
      setTechnicianActivity(response.data.technicians || []);
    } catch (error) {
      console.error('Failed to fetch technician activity:', error);
      toast.error('Failed to load technician activity report');
    } finally {
      setTechActivityLoading(false);
    }
  };

  const handleTechActivityFilter = () => {
    if (techActivityStartDate && techActivityEndDate) {
      fetchTechnicianActivity();
      toast.success('Technician activity filtered');
    } else {
      toast.error('Please select both start and end dates');
    }
  };

  const clearTechActivityFilter = () => {
    setTechActivityStartDate('');
    setTechActivityEndDate('');
    setTechnicianActivity([]);
    setExpandedTechnician(null);
  };

  const handleTechActivityExport = async (format) => {
    if (!techActivityStartDate || !techActivityEndDate) {
      toast.error('Please select a date range first');
      return;
    }
    try {
      const response = await api.get('/reports/technician-activity/export', {
        params: {
          format,
          start_date: techActivityStartDate,
          end_date: techActivityEndDate
        },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `technician_activity_report.${format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${format.toUpperCase()} exported successfully!`);
    } catch (error) {
      console.error('Failed to export:', error);
      toast.error('Failed to export technician activity report');
    }
  };

  const getSecondCheckBadge = (status) => {
    switch (status) {
      case 'Approved':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3 mr-1" />
            Approved
          </span>
        );
      case 'Rejected':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PASS':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3 mr-1" />
            PASS
          </span>
        );
      case 'HOLD':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
            <XCircle className="w-3 h-3 mr-1" />
            HOLD
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDrillDownExport = async (format) => {
    if (!drillDownData) return;
    
    try {
      let endpoint;
      let filename;
      
      if (drillDownType === 'asset') {
        endpoint = `/reports/asset-drill-down/${encodeURIComponent(drillDownData.asset_id)}/export?format=${format}`;
        filename = `asset_${drillDownData.asset_id}_history.${format === 'excel' ? 'xlsx' : 'csv'}`;
      } else {
        endpoint = `/reports/customer-drill-down/${encodeURIComponent(drillDownData.customer_name)}/export?format=${format}`;
        filename = `customer_history.${format === 'excel' ? 'xlsx' : 'csv'}`;
      }
      
      const response = await api.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${format.toUpperCase()} exported successfully!`);
    } catch (error) {
      console.error('Failed to export:', error);
      toast.error('Failed to export data');
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await api.get(`/reports/export/${format}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `qc_report.${format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${format.toUpperCase()} report downloaded successfully!`);
    } catch (error) {
      console.error('Failed to export:', error);
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="reports-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">Reports & Analytics</h1>
          <p className="text-lg text-muted-foreground">Export and analyze QC data</p>
        </div>
        <div className="flex flex-col md:flex-row gap-3">
          <Button
            onClick={() => handleExport('csv')}
            data-testid="export-csv-button"
            disabled={exporting}
            variant="outline"
            className="h-12"
          >
            <FileText className="w-5 h-5 mr-2" />
            Export CSV
          </Button>
          <Button
            onClick={() => handleExport('excel')}
            data-testid="export-excel-button"
            disabled={exporting}
            className="h-12"
          >
            <FileSpreadsheet className="w-5 h-5 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white rounded-xl border border-border shadow-sm p-6 mb-6">
        <div className="flex flex-col md:flex-row items-end gap-4">
          <div className="flex-1">
            <Label>Start Date</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-12"
              data-testid="start-date-input"
            />
          </div>
          <div className="flex-1">
            <Label>End Date</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-12"
              data-testid="end-date-input"
            />
          </div>
          <Button onClick={handleDateFilter} className="h-12 px-6" data-testid="apply-filter-button">
            <Calendar className="w-5 h-5 mr-2" />
            Apply Filter
          </Button>
          <Button onClick={clearDateFilter} variant="outline" className="h-12 px-6">
            Clear
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-3xl grid-cols-4 h-14">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="asset-deployments" data-testid="tab-asset-deployments">Asset Deploys</TabsTrigger>
          <TabsTrigger value="customer-deployments" data-testid="tab-customer-deployments">Customer Deploys</TabsTrigger>
          <TabsTrigger value="technician-activity" data-testid="tab-technician-activity">
            <Users className="w-4 h-4 mr-1 hidden md:inline" />
            Technician Activity
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          {stats && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl border border-border shadow-sm p-6">
              <p className="text-sm font-medium text-muted-foreground uppercase mb-2">Total Checks</p>
              <p className="text-4xl font-bold">{stats.total_qc_checks}</p>
            </div>
            <div className="bg-white rounded-xl border border-border shadow-sm p-6">
              <p className="text-sm font-medium text-muted-foreground uppercase mb-2">Passed</p>
              <p className="text-4xl font-bold text-green-600">{stats.pass_count}</p>
            </div>
            <div className="bg-white rounded-xl border border-border shadow-sm p-6">
              <p className="text-sm font-medium text-muted-foreground uppercase mb-2">On Hold</p>
              <p className="text-4xl font-bold text-red-600">{stats.hold_count}</p>
            </div>
            <div className="bg-white rounded-xl border border-border shadow-sm p-6">
              <p className="text-sm font-medium text-muted-foreground uppercase mb-2">Pending</p>
              <p className="text-4xl font-bold text-blue-600">{stats.pending_count}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-border shadow-sm p-6">
            <h2 className="text-2xl font-bold mb-4">Top Hold Reasons</h2>
            {stats.top_hold_reasons.length > 0 ? (
              <div className="space-y-3">
                {stats.top_hold_reasons.map((reason, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <span className="font-medium">{reason.reason}</span>
                    <span className="px-3 py-1 bg-destructive/10 text-destructive rounded-full text-sm font-bold">
                      {reason.count}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">No hold reasons yet</p>
            )}
          </div>

          <div className="bg-white rounded-xl border border-border shadow-sm p-6">
            <h2 className="text-2xl font-bold mb-4">Technician Productivity</h2>
            {stats.technician_productivity.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {stats.technician_productivity.map((tech, idx) => (
                  <div key={idx} className="p-4 bg-muted rounded-lg">
                    <p className="font-bold text-lg mb-2">{tech.technician}</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Total:</span>
                        <span className="font-bold">{tech.total_checks}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Pass:</span>
                        <span className="font-bold text-green-600">{tech.pass_count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Hold:</span>
                        <span className="font-bold text-red-600">{tech.hold_count}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">No data yet</p>
            )}
          </div>
        </div>
      )}
    </TabsContent>

    <TabsContent value="asset-deployments" className="mt-6">
      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-muted/50">
          <p className="text-sm text-muted-foreground">Click on any row to see which customers have used that asset</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-bold">Asset ID</th>
                <th className="px-6 py-4 text-left text-sm font-bold">Brand / Model</th>
                <th className="px-6 py-4 text-left text-sm font-bold">Category</th>
                <th className="px-6 py-4 text-left text-sm font-bold">Total Deployments</th>
                <th className="px-6 py-4 text-left text-sm font-bold">Dispatch / Return</th>
                <th className="px-6 py-4 text-left text-sm font-bold">Pass / Hold</th>
                <th className="px-6 py-4 text-left text-sm font-bold">Avg Score</th>
                <th className="px-6 py-4 text-left text-sm font-bold">Customers</th>
                <th className="px-6 py-4 text-right text-sm font-bold"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {assetDeployments.map((asset) => (
                <tr 
                  key={asset._id} 
                  className="hover:bg-primary/5 cursor-pointer transition-colors"
                  onClick={() => handleAssetDrillDown(asset._id)}
                  data-testid={`asset-row-${asset._id}`}
                >
                  <td className="px-6 py-4 font-bold text-primary">{asset._id}</td>
                  <td className="px-6 py-4">{asset.brand} {asset.model}</td>
                  <td className="px-6 py-4">{asset.product_category}</td>
                  <td className="px-6 py-4 text-center font-bold">{asset.total_deployments}</td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-green-600 font-bold">{asset.dispatch_count}</span> / 
                    <span className="text-blue-600 font-bold ml-1">{asset.return_count}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-green-600 font-bold">{asset.pass_count}</span> / 
                    <span className="text-red-600 font-bold ml-1">{asset.hold_count}</span>
                  </td>
                  <td className="px-6 py-4 text-center font-bold">{asset.avg_score?.toFixed(1) || '-'}</td>
                  <td className="px-6 py-4">{asset.customers?.length || 0} customers</td>
                  <td className="px-6 py-4 text-right">
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </TabsContent>

    <TabsContent value="customer-deployments" className="mt-6">
      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-muted/50">
          <p className="text-sm text-muted-foreground">Click on any row to see which assets this customer has used</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-bold">Customer Name</th>
                <th className="px-6 py-4 text-left text-sm font-bold">Total Deployments</th>
                <th className="px-6 py-4 text-left text-sm font-bold">Dispatch / Return</th>
                <th className="px-6 py-4 text-left text-sm font-bold">Unique Assets</th>
                <th className="px-6 py-4 text-left text-sm font-bold">Pass / Hold</th>
                <th className="px-6 py-4 text-left text-sm font-bold">Avg Score</th>
                <th className="px-6 py-4 text-right text-sm font-bold"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {customerDeployments.map((customer, idx) => (
                <tr 
                  key={idx} 
                  className="hover:bg-primary/5 cursor-pointer transition-colors"
                  onClick={() => handleCustomerDrillDown(customer.customer_name || customer._id)}
                  data-testid={`customer-row-${idx}`}
                >
                  <td className="px-6 py-4 font-bold">{customer.customer_name || customer._id}</td>
                  <td className="px-6 py-4 text-center font-bold">{customer.total_deployments}</td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-green-600 font-bold">{customer.dispatch_count}</span> / 
                    <span className="text-blue-600 font-bold ml-1">{customer.return_count}</span>
                  </td>
                  <td className="px-6 py-4 text-center font-bold">{customer.unique_assets_count}</td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-green-600 font-bold">{customer.pass_count}</span> / 
                    <span className="text-red-600 font-bold ml-1">{customer.hold_count}</span>
                  </td>
                  <td className="px-6 py-4 text-center font-bold">{customer.avg_score?.toFixed(1) || '-'}</td>
                  <td className="px-6 py-4 text-right">
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </TabsContent>

    {/* Technician Activity Tab */}
    <TabsContent value="technician-activity" className="mt-6">
      <div className="space-y-6">
        {/* Date Filter for Technician Activity */}
        <div className="bg-white rounded-xl border border-border shadow-sm p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-primary" />
            Technician Activity Report
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Select a date range to view detailed activity for each technician, including new QCs performed and second checks completed.
          </p>
          <div className="flex flex-col md:flex-row items-end gap-4">
            <div className="flex-1">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={techActivityStartDate}
                onChange={(e) => setTechActivityStartDate(e.target.value)}
                className="h-12"
                data-testid="tech-activity-start-date"
              />
            </div>
            <div className="flex-1">
              <Label>End Date</Label>
              <Input
                type="date"
                value={techActivityEndDate}
                onChange={(e) => setTechActivityEndDate(e.target.value)}
                className="h-12"
                data-testid="tech-activity-end-date"
              />
            </div>
            <Button 
              onClick={handleTechActivityFilter} 
              className="h-12 px-6" 
              data-testid="tech-activity-filter-btn"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Generate Report
            </Button>
            <Button onClick={clearTechActivityFilter} variant="outline" className="h-12 px-6">
              Clear
            </Button>
          </div>
        </div>

        {/* Export buttons */}
        {technicianActivity.length > 0 && (
          <div className="flex justify-end gap-3">
            <Button
              onClick={() => handleTechActivityExport('csv')}
              variant="outline"
              data-testid="tech-activity-export-csv"
            >
              <FileText className="w-5 h-5 mr-2" />
              Export CSV
            </Button>
            <Button
              onClick={() => handleTechActivityExport('excel')}
              data-testid="tech-activity-export-excel"
            >
              <FileSpreadsheet className="w-5 h-5 mr-2" />
              Export Excel
            </Button>
          </div>
        )}

        {/* Loading state */}
        {techActivityLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        )}

        {/* Results */}
        {!techActivityLoading && technicianActivity.length > 0 && (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-border shadow-sm p-6">
                <p className="text-sm font-medium text-muted-foreground uppercase mb-2">Total Technicians</p>
                <p className="text-4xl font-bold">{technicianActivity.length}</p>
              </div>
              <div className="bg-white rounded-xl border border-border shadow-sm p-6">
                <p className="text-sm font-medium text-muted-foreground uppercase mb-2">Total New QCs</p>
                <p className="text-4xl font-bold text-primary">
                  {technicianActivity.reduce((sum, t) => sum + (t.total_qc_checks || 0), 0)}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-border shadow-sm p-6">
                <p className="text-sm font-medium text-muted-foreground uppercase mb-2">Total Second Checks</p>
                <p className="text-4xl font-bold text-blue-600">
                  {technicianActivity.reduce((sum, t) => sum + (t.total_second_checks || 0), 0)}
                </p>
              </div>
            </div>

            {/* Technician Cards */}
            <div className="space-y-4">
              {technicianActivity.map((tech) => {
                const newQCs = (tech.details || []).filter(d => d.type === 'New QC');
                const secondChecks = (tech.details || []).filter(d => d.type === 'Second Check');
                
                return (
                <div 
                  key={tech.username}
                  className="bg-white rounded-xl border border-border shadow-sm overflow-hidden"
                  data-testid={`technician-card-${tech.username}`}
                >
                  {/* Technician Header */}
                  <div 
                    className="p-6 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => setExpandedTechnician(
                      expandedTechnician === tech.username ? null : tech.username
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <Users className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold">{tech.technician_name}</h3>
                          <p className="text-sm text-muted-foreground">@{tech.username}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-6">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-primary">{tech.total_qc_checks || 0}</p>
                          <p className="text-xs text-muted-foreground">New QCs</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-blue-600">{tech.total_second_checks || 0}</p>
                          <p className="text-xs text-muted-foreground">2nd Checks</p>
                        </div>
                        <ChevronRight 
                          className={`w-6 h-6 text-muted-foreground transition-transform ${
                            expandedTechnician === tech.username ? 'rotate-90' : ''
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedTechnician === tech.username && (
                    <div className="border-t bg-muted/30 p-6">
                      {/* New QCs Section */}
                      {newQCs.length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-bold text-lg mb-3 flex items-center">
                            <ClipboardCheck className="w-5 h-5 mr-2 text-primary" />
                            New QCs Performed ({newQCs.length})
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-muted">
                                <tr>
                                  <th className="px-4 py-2 text-left font-bold">Customer</th>
                                  <th className="px-4 py-2 text-left font-bold">Asset ID</th>
                                  <th className="px-4 py-2 text-left font-bold">Product</th>
                                  <th className="px-4 py-2 text-left font-bold">Event</th>
                                  <th className="px-4 py-2 text-left font-bold">Status</th>
                                  <th className="px-4 py-2 text-left font-bold">Score</th>
                                  <th className="px-4 py-2 text-left font-bold">Date/Time</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {newQCs.map((qc, idx) => (
                                  <tr key={idx} className="hover:bg-muted/50">
                                    <td className="px-4 py-2 font-medium">{qc.customer_name}</td>
                                    <td className="px-4 py-2 text-primary font-bold">{qc.asset_id}</td>
                                    <td className="px-4 py-2">{qc.product_category}</td>
                                    <td className="px-4 py-2">
                                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                        qc.qc_event === 'Dispatch' 
                                          ? 'bg-green-100 text-green-700' 
                                          : 'bg-blue-100 text-blue-700'
                                      }`}>
                                        {qc.qc_event}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2">{getStatusBadge(qc.qc_status)}</td>
                                    <td className="px-4 py-2 font-bold">{qc.qc_score || '-'}</td>
                                    <td className="px-4 py-2 text-muted-foreground">{formatDate(qc.date_time)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Second Checks Section */}
                      {secondChecks.length > 0 && (
                        <div>
                          <h4 className="font-bold text-lg mb-3 flex items-center">
                            <UserCheck className="w-5 h-5 mr-2 text-blue-600" />
                            Second Checks Completed ({secondChecks.length})
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-muted">
                                <tr>
                                  <th className="px-4 py-2 text-left font-bold">Customer</th>
                                  <th className="px-4 py-2 text-left font-bold">Asset ID</th>
                                  <th className="px-4 py-2 text-left font-bold">Product</th>
                                  <th className="px-4 py-2 text-left font-bold">Original Tech</th>
                                  <th className="px-4 py-2 text-left font-bold">Decision</th>
                                  <th className="px-4 py-2 text-left font-bold">Date/Time</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {secondChecks.map((check, idx) => (
                                  <tr key={idx} className="hover:bg-muted/50">
                                    <td className="px-4 py-2 font-medium">{check.customer_name}</td>
                                    <td className="px-4 py-2 text-primary font-bold">{check.asset_id}</td>
                                    <td className="px-4 py-2">{check.product_category}</td>
                                    <td className="px-4 py-2">{check.original_technician}</td>
                                    <td className="px-4 py-2">{getSecondCheckBadge(check.qc_status)}</td>
                                    <td className="px-4 py-2 text-muted-foreground">{formatDate(check.date_time)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {newQCs.length === 0 && secondChecks.length === 0 && (
                        <p className="text-center text-muted-foreground py-4">
                          No activity data available
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )})}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!techActivityLoading && technicianActivity.length === 0 && (
          <div className="bg-white rounded-xl border border-border shadow-sm p-12 text-center">
            <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-bold mb-2">No Report Generated Yet</h3>
            <p className="text-muted-foreground mb-4">
              Select a date range above and click "Generate Report" to view technician activity.
            </p>
          </div>
        )}
      </div>
    </TabsContent>
  </Tabs>

      {/* Drill-Down Dialog */}
      <Dialog open={showDrillDown} onOpenChange={setShowDrillDown}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <DialogTitle className="text-xl">
                {drillDownType === 'asset' 
                  ? `Asset History: ${drillDownData?.asset_id || ''}`
                  : `Customer History: ${drillDownData?.customer_name || ''}`
                }
              </DialogTitle>
              {drillDownData && drillDownData.history?.length > 0 && (
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDrillDownExport('csv')}
                    data-testid="drill-down-export-csv"
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    CSV
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDrillDownExport('excel')}
                    data-testid="drill-down-export-excel"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-1" />
                    Excel
                  </Button>
                </div>
              )}
            </div>
          </DialogHeader>
          
          {drillDownLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : drillDownData ? (
            <div className="flex-1 overflow-y-auto">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Total Deployments</p>
                  <p className="text-2xl font-bold">{drillDownData.total_deployments}</p>
                </div>
                {drillDownType === 'asset' && drillDownData.asset_details && (
                  <>
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Brand</p>
                      <p className="text-lg font-bold">{drillDownData.asset_details.brand}</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Model</p>
                      <p className="text-lg font-bold">{drillDownData.asset_details.model_name}</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Category</p>
                      <p className="text-lg font-bold">{drillDownData.asset_details.asset_type}</p>
                    </div>
                  </>
                )}
                {drillDownType === 'customer' && (
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Unique Assets</p>
                    <p className="text-2xl font-bold">{drillDownData.unique_assets}</p>
                  </div>
                )}
              </div>

              {/* History Timeline */}
              <h3 className="text-lg font-bold mb-4">Deployment History</h3>
              <div className="space-y-3">
                {drillDownData.history?.map((item, idx) => (
                  <div 
                    key={idx} 
                    className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    data-testid={`drill-down-item-${idx}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${
                          item.event_type === 'Dispatch' ? 'bg-green-500' : 'bg-blue-500'
                        }`}></div>
                        <div>
                          <p className="font-bold">
                            {drillDownType === 'asset' 
                              ? item.customer_name 
                              : `${item.asset_id} - ${item.brand || ''} ${item.model || ''}`
                            }
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {item.event_type} • {formatDate(item.date)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {getStatusBadge(item.qc_status)}
                        {item.qc_score !== null && (
                          <span className="text-sm font-bold">{item.qc_score}%</span>
                        )}
                        <span className="text-sm text-muted-foreground">by {item.technician}</span>
                      </div>
                    </div>
                    {item.hold_reasons?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {item.hold_reasons.map((reason, rIdx) => (
                          <span key={rIdx} className="px-2 py-0.5 bg-red-50 text-red-600 text-xs rounded">
                            {reason}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {drillDownData.history?.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No deployment history found</p>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsPage;